import * as React from 'react'
import { CircleStop } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { deleteJSON } from '@/lib/api'
import type { ProfileMembership } from '@/lib/contacts/types'
import { displayDate } from '@/lib/dates'

type AutopaySummary = NonNullable<ProfileMembership['autopay']>

const statusLabels: Record<AutopaySummary['status'], string> = {
  active: 'Active',
  disabled: 'Disabled',
  never_enrolled: 'Never enrolled',
  off: 'Off',
  suspended: 'Suspended',
}

const statusVariants: Record<AutopaySummary['status'], BadgeProps['variant']> = {
  active: 'success',
  disabled: 'warning',
  never_enrolled: 'neutral',
  off: 'neutral',
  suspended: 'warning',
}

export function PersonMembershipAutopay({
  autopay,
  contactId,
}: {
  autopay: AutopaySummary
  contactId: number
}) {
  const queryClient = useQueryClient()
  const [confirmingStop, setConfirmingStop] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const stopMutation = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/persons/${contactId}/membership/autopay`),
    onSuccess: async () => {
      setConfirmingStop(false)
      await queryClient.invalidateQueries({ queryKey: ['person', contactId] })
    },
  })

  async function copyManageLink() {
    if (!autopay.manage_link_url) return

    await navigator.clipboard.writeText(autopay.manage_link_url)
    setCopied(true)
  }

  return (
    <section className="px-4 py-3" aria-labelledby="autopay-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3 id="autopay-title" className="text-body font-semibold text-foreground">
              Autopay
            </h3>
            <Badge variant={statusVariants[autopay.status]}>{statusLabels[autopay.status]}</Badge>
          </div>
          {autopay.disabled_reason && autopay.disabled_on && (
            <p className="text-body-sm text-muted-foreground">
              {sentenceCase(autopay.disabled_reason)} on {displayDate(autopay.disabled_on)}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {autopay.manage_link_url && (
            <Button type="button" size="sm" variant="outline" onClick={copyManageLink}>
              Copy Manage Link
            </Button>
          )}
          {autopay.status === 'active' && (
            <Button
              type="button"
              size="sm"
              variant="quietDestructive"
              onClick={() => setConfirmingStop(true)}
            >
              <CircleStop className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Stop Autopay
            </Button>
          )}
        </div>
      </div>

      {(autopay.card || autopay.next_charge) && (
        <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-3 text-body-sm">
          {autopay.card && (
            <>
              <div>
                <dt className="text-caption text-muted-foreground">Saved Card</dt>
                <dd className="text-foreground">
                  {autopay.card.brand} ending {autopay.card.last4}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-muted-foreground">Expiry</dt>
                <dd className="text-foreground">{cardExpiry(autopay.card)}</dd>
              </div>
            </>
          )}
          {autopay.next_charge && (
            <div>
              <dt className="text-caption text-muted-foreground">Next charge</dt>
              <dd className="text-foreground">
                {autopay.next_charge.amount} on {displayDate(autopay.next_charge.charge_on)}
              </dd>
            </div>
          )}
        </dl>
      )}

      {copied && <p className="mt-2 text-caption text-success">Manage Link copied</p>}

      <AutopayStopDialog
        open={confirmingStop}
        pending={stopMutation.isPending}
        error={stopMutation.isError}
        onClose={() => setConfirmingStop(false)}
        onSubmit={() => stopMutation.mutate()}
      />
    </section>
  )
}

function AutopayStopDialog({
  open,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean
  pending: boolean
  error: boolean
  onClose: () => void
  onSubmit: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Stop Autopay for this member?</DialogTitle>
          <DialogDescription>
            Saved Card detached · future renewals paid manually · Membership unchanged
          </DialogDescription>
        </DialogHeader>
        {error && <Alert>Autopay could not be turned off</Alert>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
            Keep Autopay
          </Button>
          <Button type="button" variant="destructive" onClick={onSubmit} disabled={pending}>
            Stop Autopay now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function cardExpiry(card: NonNullable<AutopaySummary['card']>) {
  return `${String(card.exp_month).padStart(2, '0')}/${card.exp_year}`
}

function sentenceCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
